import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import BreadCrumb from "../../../components/BreadCrumb";
import NoDataFound from "../../../components/common/NoDataFound";
import { useAppSelector } from "../../../redux/hooks";
import { selectSelectedLeagueId } from "../../../redux/slice/selectedLeagueSlice";
import { fetchLeagueById } from "../../myLeagues/api/myLeaguesApi";
import { QUERY_KEY } from "../../../services/queryEndPoint";
import { Images } from "../../../utils/imageConstants";
import { resolvePublicImageUrl } from "../../../helper/helper";
import "../tournaments/tournamentsDetails/TournamentAboutTab.scss";

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="about-row">
    <span className="about-row__label">{label}</span>
    <span className="about-row__value">{children ?? "—"}</span>
  </div>
);

const formatPhone = (
  phone?: string | null,
  countryCode?: string | number | null,
): string => {
  if (!phone) return "—";
  const cleaned = String(phone).trim();
  if (countryCode != null && String(countryCode).length > 0) {
    const cc = String(countryCode).replace(/^\+/, "");
    return `+${cc} ${cleaned}`;
  }
  return cleaned;
};

const About: React.FC = () => {
  const navigate = useNavigate();
  const selectedLeagueId = useAppSelector(selectSelectedLeagueId);
  const [bannerError, setBannerError] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const { data: league, isLoading } = useQuery({
    queryKey: [QUERY_KEY.getLeagueDetails, selectedLeagueId],
    queryFn: () => fetchLeagueById(selectedLeagueId as string),
    enabled: !!selectedLeagueId,
    staleTime: 5 * 60 * 1000,
  });

  if (!selectedLeagueId) {
    return (
      <div className="main-middle-content-wrap">
        <div className="about-wrapper custom-container">
          <BreadCrumb />
          <NoDataFound message="No league selected" />
        </div>
      </div>
    );
  }

  if (isLoading && !league) {
    return (
      <div className="main-middle-content-wrap">
        <div className="about-wrapper custom-container">
          <BreadCrumb />
          <div className="about-loading">Loading league details...</div>
        </div>
      </div>
    );
  }

  if (!league) {
    return (
      <div className="main-middle-content-wrap">
        <div className="about-wrapper custom-container">
          <BreadCrumb />
          <NoDataFound message="League details not available" />
        </div>
      </div>
    );
  }

  const locationParts = [league.city, league.state, league.country].filter(Boolean);
  const locationLabel = locationParts.length > 0 ? locationParts.join(", ") : null;

  const organizerFullName = [
    league.organizer?.playerFirstName,
    league.organizer?.playerMiddleName,
    league.organizer?.playerLastName,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  const tournamentCount = Array.isArray(league.tournamentIds) ? league.tournamentIds.length : 0;

  const handleLatestTournament = () => {
    if (!league.latestTournamentId || !league.leagueId) return;
    navigate(`/my-leagues/${league.leagueId}/tournaments/${league.latestTournamentId}`);
  };

  return (
    <div className="main-middle-content-wrap">
      <div className="about-wrapper custom-container">
        <BreadCrumb />

        <div className="about-tab league-about-tab">
          {/* Hero */}
          <section className="about-card about-card--hero">
            <div className="about-hero-banner">
              {(() => {
                const bannerSrc = resolvePublicImageUrl(league.bannerUri);
                return bannerSrc && !bannerError ? (
                  <img
                    className="about-hero-banner__img"
                    src={bannerSrc}
                    alt={league.leagueName ?? "League banner"}
                    onError={() => setBannerError(true)}
                  />
                ) : (
                  <div className="about-hero-banner__placeholder" aria-hidden />
                );
              })()}
              <div className="about-hero-scrim" aria-hidden />
              <div className="about-hero-overlay about-hero-overlay--league">
                <div className="about-hero-logo">
                  {(() => {
                    const logoSrc = resolvePublicImageUrl(league.imageUri);
                    return logoSrc && !logoError ? (
                      <img
                        src={logoSrc}
                        alt={`${league.leagueName ?? "League"} logo`}
                        onError={() => setLogoError(true)}
                      />
                    ) : (
                      <img src={Images.created_image} alt="" />
                    );
                  })()}
                </div>
                <div className="about-hero-text">
                  <div className="about-hero-meta-top">
                    {league.association && (
                      <span className="about-hero-tag">{league.association}</span>
                    )}
                  </div>
                  <h2 className="about-hero-title">{league.leagueName ?? "—"}</h2>
                  {locationLabel && (
                    <div className="about-hero-dates">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                      <span>{locationLabel}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="about-hero-stats">
              <div className="about-hero-stat">
                <span className="about-hero-stat__label">Tournaments</span>
                <span className="about-hero-stat__value">{tournamentCount}</span>
              </div>
            </div>
          </section>

          <div className="about-grid">
            {/* Organizer */}
            <section className="about-card">
              <h3 className="about-card__title">Organizer</h3>
              <div className="about-card__body">
                <Row label="Name">
                  {organizerFullName || league.organizerName || "—"}
                </Row>
                <Row label="Email">
                  {league.organizerEmailId || league.organizer?.emailId || "—"}
                </Row>
                <Row label="Phone">
                  {formatPhone(
                    league.organizerPhoneNumber || league.organizer?.phoneNo,
                    league.countryCode ?? league.organizer?.countryCode,
                  )}
                </Row>
                {league.association && (
                  <Row label="Association">{league.association}</Row>
                )}
              </div>
            </section>

            {/* Location */}
            <section className="about-card">
              <h3 className="about-card__title">Location</h3>
              <div className="about-card__body">
                <Row label="City">{league.city || "—"}</Row>
                <Row label="State">{league.state || "—"}</Row>
                <Row label="Country">{league.country || "—"}</Row>
              </div>
            </section>
          </div>

          {/* Latest tournament link */}
          {league.latestTournamentId && (
            <section className="about-card about-card--full">
              <h3 className="about-card__title">Latest Tournament</h3>
              <button
                type="button"
                className="about-link-btn"
                onClick={handleLatestTournament}
              >
                View latest tournament →
              </button>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default About;
